import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  viewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { UpgraderState } from '../../services/upgrader-new.service';
import { HexagonSmokeService, HexagonSpinService, HexagonStateColor } from '../../services';
import { HexagonGlowSvgComponent } from '../../svg/hexagon-glow-svg.component';
import { HexagonEdgesSvgComponent } from '../../svg/hexagon-edges-svg.component';
import { HexagonBorderSvgComponent } from '../../svg/hexagon-border-svg.component';
import { HexagonArrowSvgComponent } from '../../svg/hexagon-arrow-svg.component';
import { HexagonBorderOuterSvgComponent } from '../../svg/hexagon-border-outer-svg.component';

@Component({
  selector: 'app-upgrader-hexagon',
  standalone: true,
  imports: [
    CommonModule,
    DecimalPipe,
    AngularSvgIconModule,
    HexagonGlowSvgComponent,
    HexagonEdgesSvgComponent,
    HexagonBorderSvgComponent,
    HexagonArrowSvgComponent,
    HexagonBorderOuterSvgComponent,
  ],
  providers: [HexagonSmokeService, HexagonSpinService],
  templateUrl: './upgrader-hexagon.component.html',
  styleUrl: './upgrader-hexagon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgraderHexagonComponent implements OnDestroy {
  readonly smokeCanvas = viewChild.required<ElementRef<HTMLCanvasElement>>('smokeCanvas');
  readonly arrowEl = viewChild<ElementRef<HTMLElement>>('arrowEl');
  readonly trailEl = viewChild<ElementRef<HTMLElement>>('trailEl');

  // Inputs
  readonly chance = input<number>(0);
  readonly state = input<UpgraderState>('idle');
  readonly roll = input<number | null>(null);
  readonly won = input<boolean | null>(null); // Backend result - source of truth
  readonly winRangeStart = input<number>(0);
  readonly winRangeEnd = input<number>(0);
  readonly isFastMode = input<boolean>(false);
  readonly wonItemImage = input<string | null>(null);
  readonly wonAmount = input<number>(0);
  readonly lostAmount = input<number>(0);

  // Outputs
  readonly animationComplete = output<{ won: boolean; roll: number }>();

  // Color constants
  private readonly COLOR_DEFAULT = '#FFAA00';
  private readonly COLOR_SUCCESS = 'rgba(133, 229, 99, 1)';
  private readonly COLOR_FAILED = 'rgba(251, 88, 88, 1)';

  // Hexagon border path length (will be calculated from SVG, fallback to estimate)
  private hexagonPathLength = 670;

  constructor(
    protected readonly smokeService: HexagonSmokeService,
    protected readonly spinService: HexagonSpinService
  ) {
    // Initialize smoke canvas when view is ready
    effect(() => {
      const canvas = this.smokeCanvas();
      this.smokeService.init(canvas.nativeElement);
      this.smokeService.start();
    });

    // Direct DOM updates for smooth animation (bypasses Angular template system)
    effect(() => {
      const rotation = this.spinService.currentRotation();
      const trailWidth = this.spinService.trailWidth();
      const arrow = this.arrowEl()?.nativeElement;
      const trail = this.trailEl()?.nativeElement;
      if (arrow) {
        arrow.style.transform = `translate(-50%, -100%) rotate(${rotation}deg)`;
      }
      if (trail) {
        trail.style.transform = `translate(-50%, -50%) rotate(${rotation}deg)`;
        trail.style.setProperty('--trail-width', `${trailWidth}deg`);
        trail.style.setProperty('--trail-mid', `${trailWidth * 0.6}deg`);
      }
    });

    // Watch for state changes to trigger animation
    effect(() => {
      const currentState = this.state();
      const rollValue = this.roll();
      const wonValue = this.won();

      // Update smoke color based on state
      this.smokeService.setStateColor(this.mapStateToColor(currentState));

      // Trigger spin animation when rolling
      if (currentState === 'rolling' && rollValue !== null && wonValue !== null) {
        this.spinService.spin(
          {
            targetRoll: rollValue,
            won: wonValue, // Use backend result - source of truth
            isFastMode: this.isFastMode(),
          },
          (result) => this.animationComplete.emit(result)
        );
      }

      // Reset arrow to default position when returning to idle
      if (currentState === 'idle') {
        this.spinService.reset();
      }
    });
  }

  onPathLengthCalculated(length: number): void {
    if (length > 0) {
      this.hexagonPathLength = length;
    }
  }

  ngOnDestroy(): void {
    this.smokeService.destroy();
    this.spinService.destroy();
  }

  // ============ Template Helpers ============

  /**
   * Get the current color based on state
   */
  getStateColor(): string {
    const currentState = this.state();
    if (currentState === 'won') {
      return this.COLOR_SUCCESS;
    } else if (currentState === 'lost') {
      return this.COLOR_FAILED;
    }
    return this.COLOR_DEFAULT;
  }

  /**
   * Get the current color with opacity for layer_6 lines
   */
  getStateColorWithOpacity(opacity: number): string {
    const currentState = this.state();
    if (currentState === 'won') {
      return `rgba(133, 229, 99, ${opacity})`;
    } else if (currentState === 'lost') {
      return `rgba(251, 88, 88, ${opacity})`;
    }
    return `rgba(255, 170, 0, ${opacity})`;
  }

  /**
   * Get stroke-dasharray for the border based on win zone
   * For won/lost states, shows full border
   */
  getBorderDashArray(): string {
    const currentState = this.state();
    if (currentState === 'won' || currentState === 'lost') {
      return `${this.hexagonPathLength} 0`;
    }
    const winStart = this.winRangeStart();
    const winEnd = this.winRangeEnd();
    const winZonePercent = (winEnd - winStart) / 100;
    const visibleLength = winZonePercent * this.hexagonPathLength;
    return `${visibleLength} ${this.hexagonPathLength}`;
  }

  /**
   * Get stroke-dashoffset to position the win zone at correct location
   */
  getBorderDashOffset(): number {
    const winStart = this.winRangeStart();
    const startOffset = (winStart / 100) * this.hexagonPathLength;
    return -startOffset;
  }

  // ============ Private Helpers ============

  private mapStateToColor(state: UpgraderState): HexagonStateColor {
    if (state === 'won') return 'won';
    if (state === 'lost') return 'lost';
    return 'default';
  }
}
